import { showToast } from '@/components/Layout/Toast';
import type { UseQueryResult } from '@tanstack/react-query';

/* ------------------------------------------------------------------ */
/*                           Shared helpers                            */
/* ------------------------------------------------------------------ */

/** Registry keyed by the stable `queryHash` so we don’t toast twice   *
 *  across re‑renders.                                                */
type Stamp = { success?: number; error?: number };
const queryHandled = new Map<string, Stamp>();

/** Safely pull a message out of `unknown` errors. */
const getErrMessage = (err: unknown, fallback: string): string =>
    typeof err === 'object' && err !== null && 'message' in err ? String((err as any).message) : fallback;

const hasSuccessFlag = (
    data: unknown
): data is {
    success: boolean;
    errorMessages?: string[];
} => typeof data === 'object' && data !== null && 'success' in data;

/* ------------------------------------------------------------------ */
/*                       handleTrpcQuery (v3)                          */
/* ------------------------------------------------------------------ */

export interface HandleTrpcQueryOptions<TData> {
    onSuccess?: (data: TData) => void;
    successMessage?: string;
    showSuccessToast?: boolean;

    onError?: (err: Error) => void;
    fallbackErrorMessage?: string;
    showErrorToast?: boolean;

    toastFn?: typeof showToast;
    setError?: (msg: string) => void;
}

export function handleTrpcQuery<TData extends { success: boolean; errorMessages?: string[] }, TError = unknown>(
    query: UseQueryResult<TData, TError>,
    opts?: HandleTrpcQueryOptions<TData>
): TData | null;

export function handleTrpcQuery<TData, TError = unknown>(
    query: UseQueryResult<TData, TError>,
    opts?: HandleTrpcQueryOptions<TData>
): TData | null;

export function handleTrpcQuery<TData, TError = unknown>(
    query: UseQueryResult<TData, TError>,
    opts: HandleTrpcQueryOptions<TData> = {}
): TData | null {
    const {
        onSuccess,
        successMessage,
        showSuccessToast = false,

        onError,
        fallbackErrorMessage = 'An error occurred',
        showErrorToast = true,

        toastFn = showToast,
        setError
    } = opts;

    // Stable key across renders
    const hash = String((query as any).queryHash ?? '');

    /* --------- loading ------------------------------------------------ */
    if (query.isLoading) return null;

    /* --------- error -------------------------------------------------- */
    if (query.isError && query.error) {
        const stamp = queryHandled.get(hash) ?? {};
        if (stamp.error === query.errorUpdatedAt) return null; // already handled

        stamp.error = query.errorUpdatedAt!;
        queryHandled.set(hash, stamp);

        const msg = getErrMessage(query.error, fallbackErrorMessage);

        if (showErrorToast) toastFn(msg, 'error');
        setError?.(msg);
        onError?.(new Error(msg));
        return null;
    }

    /* --------- success ------------------------------------------------ */
    if (query.isSuccess && query.data) {
        const stamp = queryHandled.get(hash) ?? {};
        if (stamp.success === query.dataUpdatedAt) return query.data; // handled

        stamp.success = query.dataUpdatedAt!;
        queryHandled.set(hash, stamp);

        if (hasSuccessFlag(query.data) && query.data.success === false && query.data.errorMessages?.length) {
            if (Array.isArray(query.data.errorMessages) && query.data.errorMessages.length) {
                let msg = '';

                query.data.errorMessages.forEach((m) => {
                    if (msg) msg += '\n';
                    msg += m || fallbackErrorMessage;
                    if (showErrorToast) toastFn(msg, 'error');
                    setError?.(msg);
                });
            } else {
                const msg = query.data.errorMessages[0] || fallbackErrorMessage;
                if (showErrorToast) toastFn(msg, 'error');
                setError?.(msg);
            }

            return null;
        }

        if (showSuccessToast && successMessage) toastFn(successMessage, 'success');
        onSuccess?.(query.data);
        return query.data;
    }

    return null;
}
